import React, { useState } from 'react';
import {
  Upload,
  Loader2,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Lightbulb,
  Sparkles,
  Search,
  MessageSquare,
  Briefcase,
  Users,
  ShieldCheck,
  FileText,
  X,
  Presentation,
  Video,
} from 'lucide-react';
import api from '../api';

// ─── Sales Call Coach (hidden/beta) ──────────────────────────────────────────
// Not linked from app nav — reachable only at /app/sales-call-coach for
// whoever has the direct link (see frontend/src/App.tsx).
//
// Scores an uploaded/pasted sales call transcript against two named public
// sales frameworks:
//   - Nate Nasralla (Fluint.io) — One-Page Business Case, champion enablement
//   - Chris Orlob (ex-Gong) — 4-question discovery, FAVORITE demo, objection
//     handling

interface Dimension {
  name: string;
  framework: string;
  score: number | null;
  feedback: string;
}

interface CoachResult {
  overall_score: number;
  dimensions: Dimension[];
  strengths: string[];
  improvements: string[];
  next_framework_recommendation: string;
  deck_alignment?: string;
  video_alignment?: string;
}

function scoreColor(score: number | null): string {
  if (score === null) return 'text-gray-400';
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
}

function scoreBarColor(score: number | null): string {
  if (score === null) return '#d1d5db';
  if (score >= 80) return '#059669';
  if (score >= 60) return '#d97706';
  return '#dc2626';
}

function scoreRingColor(score: number): string {
  if (score >= 80) return '#059669';
  if (score >= 60) return '#d97706';
  return '#dc2626';
}

function scoreLabel(score: number): string {
  if (score >= 85) return 'Excellent call';
  if (score >= 70) return 'Solid call';
  if (score >= 55) return 'Needs work';
  return 'Off track';
}

const DIMENSION_ICONS: Record<string, React.ElementType> = {
  'Discovery Quality': Search,
  'Value Communication': MessageSquare,
  'Business Case Building': Briefcase,
  'Champion Development': Users,
  'Objection Handling': ShieldCheck,
};

function frameworkBadgeClasses(framework: string): string {
  if (framework.toLowerCase().includes('nasralla')) {
    return 'bg-violet-50 text-violet-700 border-violet-200';
  }
  return 'bg-blue-50 text-blue-700 border-blue-200';
}

const ScoreRing: React.FC<{ score: number; size?: number }> = ({ score, size = 128 }) => {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(Math.max(score, 0), 100) / 100);
  const color = scoreRingColor(score);

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#e5e7eb" strokeWidth={strokeWidth} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color }}>
          {score}
        </span>
        <span className="text-xs text-gray-400">/ 100</span>
      </div>
    </div>
  );
};

const SalesCallCoach: React.FC = () => {
  const [transcript, setTranscript] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [deck, setDeck] = useState<File | null>(null);
  const [video, setVideo] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CoachResult | null>(null);

  const acceptFile = (f: File) => {
    setFile(f);
    setTranscript('');
    setVideo(null);
    setError(null);
  };

  const acceptVideo = (f: File) => {
    // A video replaces the transcript entirely (we derive it via Whisper) and
    // makes a separately-uploaded deck redundant (the video's own on-screen
    // OCR is a better, timestamped alignment source) — clear both.
    setVideo(f);
    setTranscript('');
    setFile(null);
    setDeck(null);
    setError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) acceptFile(f);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.toLowerCase().endsWith('.txt')) acceptFile(f);
  };

  const handleDeckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setDeck(f);
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) acceptVideo(f);
  };

  const handleAnalyze = async () => {
    if (!transcript.trim() && !file && !video) {
      setError('Paste a transcript, upload a .txt file, or upload a video recording first.');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const formData = new FormData();
      if (video) {
        formData.append('video', video);
      } else if (file) {
        formData.append('file', file);
      } else {
        formData.append('transcript', transcript.trim());
      }
      if (deck && !video) {
        formData.append('deck', deck);
      }
      const resp = await api.post('/api/sales-call-coach/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10 * 60 * 1000,
      });
      if (resp.data?.success) {
        setResult(resp.data.data);
      } else {
        setError(resp.data?.error || 'Analysis failed.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Analysis failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-electric-blue/20 to-violet-100 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-electric-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-super-dark-purple leading-tight">Sales Call Coach</h1>
            <span className="inline-block text-[10px] font-semibold tracking-wide uppercase text-electric-blue bg-electric-blue/10 px-2 py-0.5 rounded-full">
              Beta
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2 mb-8 max-w-2xl">
          Scores a call transcript against Nate Nasralla's (Fluint.io) and Chris Orlob's (ex-Gong) public sales
          frameworks — discovery, value communication, business-case building, champion development, and objection
          handling.
        </p>

        {!result && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <fieldset disabled={!!video} className={video ? 'opacity-40 pointer-events-none' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Paste the call transcript</label>
              <textarea
                className="w-full h-56 border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue/20 resize-none"
                placeholder="Paste the full sales call transcript here..."
                value={transcript}
                onChange={(e) => {
                  setTranscript(e.target.value);
                  setFile(null);
                }}
                disabled={loading}
              />

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 font-medium">OR</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {file ? (
                <div className="flex items-center justify-between gap-2 border border-electric-blue/40 bg-electric-blue/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-electric-blue flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    aria-label="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  className={`flex items-center justify-center gap-2 text-sm border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
                    dragActive
                      ? 'border-electric-blue bg-electric-blue/5 text-electric-blue'
                      : 'border-gray-200 text-gray-500 hover:border-electric-blue/50 hover:bg-gray-50'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  Drop a .txt transcript here, or click to browse
                  <input type="file" accept=".txt" className="hidden" onChange={handleFileChange} disabled={loading} />
                </label>
              )}

              <div className="mt-5 pt-5 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slide deck <span className="text-gray-400 font-normal">(optional — if you shared one on this call)</span>
                </label>
                {deck ? (
                  <div className="flex items-center justify-between gap-2 border border-electric-blue/40 bg-electric-blue/5 rounded-xl p-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <Presentation className="w-4 h-4 text-electric-blue flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate">{deck.name}</span>
                    </div>
                    <button
                      onClick={() => setDeck(null)}
                      disabled={loading}
                      className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                      aria-label="Remove slide deck"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 text-sm border-2 border-dashed rounded-xl p-4 cursor-pointer text-gray-500 border-gray-200 hover:border-electric-blue/50 hover:bg-gray-50 transition-colors">
                    <Presentation className="w-4 h-4" />
                    Upload a .pptx slide deck
                    <input type="file" accept=".pptx" className="hidden" onChange={handleDeckChange} disabled={loading} />
                  </label>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  We'll match slide content to what's said in the transcript to check how well the deck reinforced the call.
                </p>
              </div>
            </fieldset>

            <div className="mt-5 pt-5 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or upload a video recording <span className="text-gray-400 font-normal">(replaces the transcript + deck above — we transcribe it and read what's on screen)</span>
              </label>
              {video ? (
                <div className="flex items-center justify-between gap-2 border border-electric-blue/40 bg-electric-blue/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <Video className="w-4 h-4 text-electric-blue flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{video.name}</span>
                  </div>
                  <button
                    onClick={() => setVideo(null)}
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    aria-label="Remove video"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 text-sm border-2 border-dashed rounded-xl p-4 cursor-pointer text-gray-500 border-gray-200 hover:border-electric-blue/50 hover:bg-gray-50 transition-colors">
                  <Video className="w-4 h-4" />
                  Upload a video (.mp4, .webm, .mov, .avi)
                  <input type="file" accept=".mp4,.webm,.mov,.avi" className="hidden" onChange={handleVideoChange} disabled={loading} />
                </label>
              )}
              <p className="text-xs text-gray-400 mt-2">
                Up to 500MB, up to 2 hours. Transcription + screen-reading can take several minutes for a long call.
              </p>
            </div>

            {error && (
              <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              onClick={handleAnalyze}
              disabled={loading || (!transcript.trim() && !file && !video)}
              className="mt-5 w-full flex items-center justify-center gap-2 bg-electric-blue text-white font-medium py-2.5 rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity shadow-sm"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing call...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Analyze Call
                </>
              )}
            </button>
          </div>
        )}

        {result && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex items-center justify-between gap-6 flex-wrap">
              <div className="flex items-center gap-5">
                <ScoreRing score={result.overall_score} />
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Overall Score</div>
                  <div className={`text-lg font-bold ${scoreColor(result.overall_score)}`}>
                    {scoreLabel(result.overall_score)}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setResult(null);
                  setTranscript('');
                  setFile(null);
                  setDeck(null);
                  setVideo(null);
                }}
                className="text-sm font-medium text-electric-blue hover:underline whitespace-nowrap"
              >
                Analyze another call
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Scored by Framework
              </h2>
              <div className="space-y-3">
                {result.dimensions.map((d, i) => {
                  const Icon = DIMENSION_ICONS[d.name] || TrendingUp;
                  return (
                    <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="flex items-start gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Icon className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-gray-800">{d.name}</div>
                            <span
                              className={`inline-block text-[11px] font-medium border rounded-full px-2 py-0.5 mt-1 ${frameworkBadgeClasses(
                                d.framework
                              )}`}
                            >
                              {d.framework}
                            </span>
                          </div>
                        </div>
                        <div className={`text-xl font-bold flex-shrink-0 ${scoreColor(d.score)}`}>
                          {d.score === null ? 'N/A' : d.score}
                        </div>
                      </div>
                      {d.score !== null && (
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-2 mb-3">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${d.score}%`,
                              backgroundColor: scoreBarColor(d.score),
                              transition: 'width 0.6s ease',
                            }}
                          />
                        </div>
                      )}
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">{d.feedback}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> What went well
                </h2>
                <ul className="space-y-2.5">
                  {result.strengths.map((s, i) => (
                    <li key={i} className="text-sm text-gray-600 flex gap-2.5 leading-relaxed">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" /> Where to improve
                </h2>
                <ul className="space-y-2.5">
                  {result.improvements.map((s, i) => (
                    <li key={i} className="text-sm text-gray-600 flex gap-2.5 leading-relaxed">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {result.deck_alignment && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Presentation className="w-4 h-4" /> Deck Alignment
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed">{result.deck_alignment}</p>
              </div>
            )}

            {result.video_alignment && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Video className="w-4 h-4" /> Screen Alignment
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed">{result.video_alignment}</p>
              </div>
            )}

            <div className="bg-gradient-to-br from-electric-blue/5 to-violet-50 border border-electric-blue/20 rounded-2xl p-6">
              <h2 className="text-sm font-semibold text-super-dark-purple mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" /> For your next call
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">{result.next_framework_recommendation}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesCallCoach;
